/**
 * The one place the dashboard talks to the API.
 *
 * ── How the session cookie reaches the API from a static SPA (ADR-002 §4.3) ──
 *
 * It reaches it **by itself, because there is only one origin.** `src/server.ts`
 * serves `web/dist` and `/api/v1/**` from the same Express app on the same host, so
 * a request to a path-relative `/api/v1/...` is same-origin and the browser attaches
 * `wp_session` — `HttpOnly; Secure; SameSite=Lax; Path=/` — with no cooperation from
 * this file. `credentials: 'same-origin'` is written out anyway: it is already the
 * fetch default, and stating it means a future reader does not have to know that.
 *
 * There is no `Authorization` header, no bearer token, and nothing in
 * `localStorage`. SEC-25 is explicit — "Không lưu token ở `localStorage`" — and the
 * cookie being `HttpOnly` means this code could not read it even if it wanted to.
 *
 * ── What that means for CSRF ────────────────────────────────────────────────
 *
 * An ambient cookie is exactly what CSRF exploits, so API_CONTRACT §1.10 puts two
 * independent controls in front of it and this client honours both:
 *
 *  1. `SameSite=Lax` — the browser will not attach `wp_session` to a cross-site
 *     POST at all. Set by the server; nothing here can weaken it.
 *  2. `X-CSRF-Token` on every unsafe method. Task 14 mints it as
 *     `HMAC(csrfKey, sessionToken)` and returns it in the login body and in
 *     `GET /auth/me`. A MAC of the whole session token means a token lifted from
 *     another session does not verify, and a `session_epoch` bump invalidates it
 *     along with the cookie.
 *
 * The token is held in a **closure, in memory**, for the lifetime of the tab. Not in
 * `localStorage` (SEC-25), not in a cookie readable by script, and not in the URL.
 * On a reload it is gone and is re-read from `GET /auth/me`, which is a request the
 * shell makes anyway — so nothing is persisted to save a round trip that is already
 * happening. A custom header also cannot be set by a cross-origin form post, which
 * is the second half of why the header is the control and not a hidden field.
 */

import { CLIENT_TRANSPORT_FAILED, toApiError, WorkforceApiError } from './errors';
import type { SessionUser } from './types';

/** API_CONTRACT §1.10. Lower-case because `Headers` normalises anyway. */
export const CSRF_HEADER = 'x-csrf-token';

/** API_CONTRACT §1.3 — echoed on every response, including errors. */
export const CORRELATION_ID_HEADER = 'x-correlation-id';

export const AUTH_LOGIN_PATH = '/api/v1/auth/login';
export const AUTH_LOGOUT_PATH = '/api/v1/auth/logout';
export const AUTH_ME_PATH = '/api/v1/auth/me';

const SAFE_METHODS: ReadonlySet<string> = new Set(['GET', 'HEAD', 'OPTIONS']);

export interface ApiClient {
  /** The CSRF token this client will send. `null` until a session body supplies one. */
  readonly csrfToken: () => string | null;
  readonly setCsrfToken: (token: string | null) => void;
  readonly request: <T>(path: string, init?: ApiRequestInit) => Promise<T>;
  /**
   * The same request, with the two response headers a caller may legitimately need.
   *
   * Added by WP-L5 (Task 53) for one reason: `PATCH /incidents/{id}` takes a
   * **mandatory** `If-Match`, and API_CONTRACT §6.9 says the precondition is
   * `updated_at` "mang trên dây đúng như PostgreSQL in ra … chính xác tới
   * micro-giây". A client that rebuilt the token from the response *body* would
   * have to parse a timestamp, and every JavaScript route through `Date` has
   * millisecond resolution — the last three digits vanish and every precondition
   * is refused `400` for being the wrong shape. So the token is taken from the
   * header, verbatim, and echoed back verbatim. `request` stays the normal case.
   */
  readonly requestWithHeaders: <T>(path: string, init?: ApiRequestInit) => Promise<ApiResponse<T>>;
  /**
   * The two calls WP-W2 (the candidate CV documents) needs and `request` cannot make.
   *
   * `putPresigned` uploads bytes **directly to object storage** through a presigned
   * PUT URL the register endpoint returned. It is not an API call — the host is object
   * storage, not this app — so it carries **no** session cookie, no CSRF header and no
   * JSON `content-type`: the URL is itself the credential, and adding any of ours would
   * either be ignored or break the signature. `credentials: 'omit'` states that.
   *
   * `sensitiveDownload` is the one place this client **follows** a redirect. `GET
   * /recruiting/candidates/{id}/documents/{docId}/download` is `x-wp-sensitive-access`
   * and takes a **mandatory** `X-Access-Reason`, so — unlike a report download — it
   * cannot be reached by a plain navigation (a navigation carries no header) and has no
   * `mode=json` escape. So the reason is sent as a header on a `fetch`, the browser
   * follows the `302` to the short-lived presigned GET, and the **bytes** come back as
   * a `Blob`. The signed URL is followed internally and **never enters JavaScript** —
   * the same discipline `reports/download.ts` keeps by never holding one — so nothing
   * here can render a stale link. A refusal (a missing reason among them) arrives as a
   * `WorkforceApiError` off the JSON body, exactly as elsewhere.
   */
  readonly putPresigned: (url: string, body: Blob) => Promise<void>;
  readonly sensitiveDownload: (path: string, reason: string) => Promise<Blob>;
  readonly login: (credentials: { username: string; password: string }) => Promise<SessionUser>;
  readonly logout: () => Promise<void>;
  readonly me: (init?: { signal?: AbortSignal }) => Promise<SessionUser>;
}

/** A body plus the headers a caller is allowed to act on. */
export interface ApiResponse<T> {
  readonly body: T;
  /**
   * The `ETag` **exactly as sent, minus the quoting HTTP requires**.
   *
   * `null` when the response carried none, which is a different fact from "the
   * resource has no version": `POST /incidents/{id}/transition` deliberately takes
   * no `If-Match`, and `Incident.version` is not populated in v1 at all.
   */
  readonly etag: string | null;
  readonly correlationId: string | null;
}

export interface ApiRequestInit {
  readonly method?: string;
  readonly body?: unknown;
  readonly signal?: AbortSignal;
  /**
   * Extra request headers, merged over the defaults.
   *
   * The two this build sends are `X-Access-Reason` — mandatory in v1 on every
   * sensitive-access read, at least 10 characters after trimming — and `If-Match`.
   * `content-type`, `accept` and `x-csrf-token` are still decided here: a caller
   * that could overwrite the CSRF header would be a second authorisation path.
   */
  readonly headers?: Readonly<Record<string, string>>;
}

/** Headers a caller may not set, because this file decides them. */
const RESERVED_HEADERS: ReadonlySet<string> = new Set([
  'accept',
  'content-type',
  CSRF_HEADER,
  'cookie',
  'authorization',
]);

export interface ApiClientOptions {
  /** Injected so tests drive a stub without touching a global. */
  readonly fetchImpl?: typeof fetch;
}

export function createApiClient(options: ApiClientOptions = {}): ApiClient {
  const doFetch = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  let csrf: string | null = null;

  async function requestWithHeaders<T>(
    path: string,
    init: ApiRequestInit = {},
  ): Promise<ApiResponse<T>> {
    const method = init.method ?? 'GET';
    const headers = new Headers({ accept: 'application/json' });

    if (init.headers !== undefined) {
      for (const [name, value] of Object.entries(init.headers)) {
        // Lower-cased before the check, because `Headers` normalises on `set` and a
        // caller writing `If-Match` must not be able to slip past a lower-case list.
        if (RESERVED_HEADERS.has(name.toLowerCase())) continue;
        headers.set(name, value);
      }
    }

    if (!SAFE_METHODS.has(method)) {
      if (csrf !== null) headers.set(CSRF_HEADER, csrf);
      // No token and an unsafe method still sends the request. The server answers
      // `403 WORKFORCE_CSRF_FAILED` with `details.reason = 'header_missing'`, which
      // is a diagnosis; refusing here would produce a client-side error that no
      // server log ever saw and that support cannot correlate.
    }
    if (init.body !== undefined) headers.set('content-type', 'application/json');

    let response: Response;
    try {
      response = await doFetch(path, {
        method,
        headers,
        // Same-origin by construction; written out rather than relied on. See the
        // module comment.
        credentials: 'same-origin',
        // A redirect is refused rather than followed. There is exactly one in the
        // contract — `GET /reports/{id}/download` answers `302` to a signed object
        // URL — and following it here would hand a JSON parser an S3 response body.
        //
        // **Task 46 (WP-K8): use `?mode=json`.** `openapi.yaml` describes it as being
        // "for clients that cannot follow a redirect to a different host", which is
        // this one by choice: the endpoint returns `200` with the signed URL in the
        // body, and the browser is then sent to it as a navigation. That keeps the
        // download out of `fetch` entirely, which is also what makes
        // `Content-Disposition` work.
        redirect: 'error',
        ...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
        ...(init.signal === undefined ? {} : { signal: init.signal }),
      });
    } catch (cause) {
      // An aborted request is the caller's own doing — a screen unmounting, a newer
      // request superseding this one — and must not be reported as an outage.
      if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
      throw new WorkforceApiError({
        code: CLIENT_TRANSPORT_FAILED,
        message: cause instanceof Error ? cause.message : 'fetch failed',
        status: 0,
        correlationId: null,
      });
    }

    const correlationId = response.headers.get(CORRELATION_ID_HEADER);
    // Unquoted here and nowhere else, so exactly one function knows the wire form.
    // The weak-validator prefix is stripped too: `src/modules/incidents/routes.ts`
    // accepts `W/"…"` and the shape check runs on the unquoted value either way.
    const rawEtag = response.headers.get('etag');
    const etag =
      rawEtag === null
        ? null
        : rawEtag.trim().replace(/^W\//, '').replace(/^"/, '').replace(/"$/, '');

    if (response.status === 204) return { body: undefined as T, etag, correlationId };

    let body: unknown = null;
    try {
      body = (await response.json()) as unknown;
    } catch {
      body = null;
    }

    if (!response.ok) throw toApiError(response.status, body, correlationId);
    return { body: body as T, etag, correlationId };
  }

  async function request<T>(path: string, init: ApiRequestInit = {}): Promise<T> {
    const response = await requestWithHeaders<T>(path, init);
    return response.body;
  }

  async function putPresigned(url: string, body: Blob): Promise<void> {
    let response: Response;
    try {
      response = await doFetch(url, {
        method: 'PUT',
        // No `content-type` set here on purpose: the browser derives one from the
        // `Blob`, and imposing one this client invented could disagree with what the
        // URL was signed for. The presigned URL is the whole authorisation.
        body,
        credentials: 'omit',
        redirect: 'error',
      });
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
      throw new WorkforceApiError({
        code: CLIENT_TRANSPORT_FAILED,
        message: cause instanceof Error ? cause.message : 'upload failed',
        status: 0,
        correlationId: null,
      });
    }
    if (!response.ok) {
      // Object storage does not speak the Workforce error envelope, so there is no
      // `code` to read; a failed upload is a transport failure carrying its status.
      throw new WorkforceApiError({
        code: CLIENT_TRANSPORT_FAILED,
        message: `object storage rejected the upload (HTTP ${String(response.status)})`,
        status: response.status,
        correlationId: null,
      });
    }
  }

  async function sensitiveDownload(path: string, reason: string): Promise<Blob> {
    let response: Response;
    try {
      response = await doFetch(path, {
        method: 'GET',
        // `X-Access-Reason` is not in `RESERVED_HEADERS`, but this call builds its own
        // `Headers` rather than going through `requestWithHeaders`, so there is nothing
        // to reserve it against. `accept: '*/*'` because what comes back after the
        // redirect is a file, not JSON.
        headers: new Headers({ accept: '*/*', 'X-Access-Reason': reason }),
        credentials: 'same-origin',
        // The one place this client follows a redirect — see `sensitiveDownload` in the
        // interface docblock. The browser resolves the `302` to the presigned GET and
        // the signed URL never surfaces here.
        redirect: 'follow',
      });
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
      throw new WorkforceApiError({
        code: CLIENT_TRANSPORT_FAILED,
        message: cause instanceof Error ? cause.message : 'download failed',
        status: 0,
        correlationId: null,
      });
    }
    if (!response.ok) {
      const correlationId = response.headers.get(CORRELATION_ID_HEADER);
      let body: unknown = null;
      try {
        body = (await response.json()) as unknown;
      } catch {
        body = null;
      }
      throw toApiError(response.status, body, correlationId);
    }
    return response.blob();
  }

  return {
    csrfToken: () => csrf,
    setCsrfToken: (token) => {
      csrf = token;
    },
    request,
    requestWithHeaders,
    putPresigned,
    sensitiveDownload,
    async login(credentials) {
      const session = await request<SessionUser>(AUTH_LOGIN_PATH, {
        method: 'POST',
        body: credentials,
      });
      // The login body is a `SessionUser` rendered by the same server function as
      // `/auth/me`, so it carries the CSRF token and the shell needs no second call.
      csrf = session.csrf_token;
      return session;
    },
    async logout() {
      // `undefined`, not `void`: `204` returns `undefined as T` a few lines up, so
      // this is what the call actually resolves to. `void` also happens to be what
      // `@typescript-eslint/no-invalid-void-type` rejects as a call-site type
      // argument, and the accurate spelling is the one that satisfies it.
      await request<undefined>(AUTH_LOGOUT_PATH, { method: 'POST' });
      csrf = null;
    },
    async me(init) {
      const session = await request<SessionUser>(AUTH_ME_PATH, {
        ...(init?.signal === undefined ? {} : { signal: init.signal }),
      });
      csrf = session.csrf_token;
      return session;
    },
  };
}
